/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo, useState } from "react";
import { Unless } from "react-if";
import { Box, Button, Col, Row } from "@nordcloud/gnui";
import { BreadcrumbsBox } from "~/components";
import { useToggle, useEstateFetch } from "~/hooks";
import { useCustomers } from "~/services/customers";
import { inflect, isEmpty } from "~/tools";
import { AttachToEnv } from "./AttachToEnv";
import { EstateRecords } from "./EstateRecords";
import { getMultiResources } from "./helpers";

export function EstatePage() {
  const { isAdminLite } = useCustomers();
  const [selectedResources, setSelectedResources] = useState<string[]>([]);
  const [showAttachmentSidebar, toggleShowAttachmentSidebar] = useToggle(false);

  const hasNoSelectedResources = isEmpty(selectedResources ?? []);

  const { result, ...filters } = useEstateFetch({});
  const { data, refetch } = result;

  const resources = useMemo(
    () => getMultiResources(selectedResources, data?.resources),
    [selectedResources, data?.resources]
  );

  const refresh = () => {
    setSelectedResources([]);
    refetch();
  };

  const excludedColumns = isAdminLite
    ? ["selection", "application", "actions"]
    : [];

  return (
    <Row>
      <Col>
        <BreadcrumbsBox title="Estate Records">
          <Unless condition={isAdminLite}>
            <Button
              disabled={hasNoSelectedResources}
              icon="link"
              onClick={toggleShowAttachmentSidebar}
            >
              {hasNoSelectedResources
                ? "Select multiple resources"
                : `Attach Selected Estate ${inflect("Record")(
                    selectedResources.length
                  )}`}
            </Button>
            <AttachToEnv
              allowMultipleAssignment
              resources={resources}
              refresh={refresh}
              isOpen={showAttachmentSidebar}
              saveSelections={false}
              onClose={toggleShowAttachmentSidebar}
            />
          </Unless>
        </BreadcrumbsBox>
        <Box spacing="spacing04">
          <EstateRecords
            allowSavingFilters
            setSelectedResources={setSelectedResources}
            selectedResources={selectedResources}
            queryFilters={filters}
            dataEstate={result}
            excludeColumns={excludedColumns}
          />
        </Box>
      </Col>
    </Row>
  );
}
